import { type FormEvent, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import AuthShell from "../components/AuthShell";
import { HxAlert, HxButton, HxInput } from "../components/hx";
import { Field, FieldGroup, FieldLabel } from "../components/ui/field";
import { PasswordInput } from "../components/ui/password-input";
import { ApiError, opsFetch } from "../api";
import { clearSavedReg } from "./voice/SipContext";
import { rememberSsoContinue, resumeSsoContinue } from "../sso-continue";

interface Props {
  onAuthChanged: () => Promise<void> | void;
  /** Copy overrides, so the /admin console reuses this form rather than
   *  carrying a hundred-line copy of it that drifts. Everything else here is
   *  router-relative already: under basename "/admin", /login/forgot and the
   *  post-sign-in "/" resolve inside that app on their own. */
  title?: string;
  subtitle?: string;
}

interface LoginResponse {
  nextStep: "totp" | "home";
}

export default function Login({ onAuthChanged, title, subtitle }: Props) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { search } = useLocation();

  // Another product sent the user here to sign in (?continue=): keep where to
  // return to across the password and two-factor steps. See sso-continue.ts.
  useEffect(() => rememberSsoContinue(search), [search]);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError(null);

    try {
      const res = await opsFetch<LoginResponse>("/ops/auth/login", {
        method: "POST",
        json: { email, password },
      });
      // A different person may be signing in on this browser. The softphone
      // credential is stored per browser, not per user, so anything left over
      // belongs to whoever was here before — and inheriting it would register
      // this user on someone else's extension.
      clearSavedReg();
      if (res.nextStep === "totp") {
        navigate("/login/totp", { replace: true });
      } else {
        await onAuthChanged();
        if (!resumeSsoContinue()) navigate("/", { replace: true });
      }
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 429 || err.code === "rate_limited") {
          setError("Too many attempts. Try again in a few minutes.");
        } else if (err.code === "not_implemented") {
          setError("Login not wired up yet (Phase 0 step 4 pending).");
        } else {
          setError("Invalid email or password.");
        }
      } else {
        setError("Something went wrong. Try again.");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell
      title={title ?? "Sign in to /ops"}
      subtitle={
        subtitle ?? "Enter the email and password for your ops account. Two-factor follows."
      }
    >
      {/* The template's login form (auth/_components/login-form.tsx): the
          Field layout, label over control. Record:
          docs/testing/ui-migration/085-login.md. */}
      <form onSubmit={submit} autoComplete="on" className="flex flex-col gap-4">
        <FieldGroup className="gap-4">
          <Field className="gap-1.5">
            <FieldLabel htmlFor="email">Email</FieldLabel>
            <HxInput
              id="email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              className="w-full"
            />
          </Field>
          <Field className="gap-1.5">
            <div className="flex items-center justify-between">
              <FieldLabel htmlFor="password">Password</FieldLabel>
              <Link
                to="/login/forgot"
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                Forgot password?
              </Link>
            </div>
            <PasswordInput
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </Field>
        </FieldGroup>
        <HxButton type="submit" variant="primary" disabled={busy} className="w-full">
          {busy ? "Signing in…" : "Continue"}
        </HxButton>
        {error && <HxAlert className="mb-0" aria-live="polite">{error}</HxAlert>}
      </form>
    </AuthShell>
  );
}
